import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Check, X, Trash2, Paperclip } from "lucide-react";

const TYPES = [
  { id: "vacation", label: "Urlaub" },
  { id: "sick", label: "Krank" },
  { id: "special", label: "Sonderurlaub" },
  { id: "training", label: "Fortbildung" },
  { id: "maternity", label: "Mutterschutz/Elternzeit" },
];

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

export default function AbsencesPage() {
  const { user, isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ type: "vacation", start_date: "", end_date: "", reason: "", attachment_b64: null, attachment_name: null });

  const load = async () => {
    const r = await api.get("/absences");
    setItems(r.data);
    if (isManager) {
      const r2 = await api.get("/users");
      setUsers(r2.data);
    }
  };
  useEffect(() => { load(); }, [isManager]);

  const create = async () => {
    try {
      await api.post("/absences", form);
      toast.success("Antrag eingereicht");
      setDialog(false);
      setForm({ type: "vacation", start_date: "", end_date: "", reason: "", attachment_b64: null, attachment_name: null });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const decide = async (id, approved) => {
    try {
      await api.post(`/absences/${id}/decide`, { approved });
      toast.success(approved ? "Genehmigt" : "Abgelehnt");
      await load();
    } catch (err) { toast.error("Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Löschen?")) return;
    try { await api.delete(`/absences/${id}`); toast.success("Gelöscht"); await load(); } catch (err) { toast.error("Fehler"); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, attachment_b64: reader.result, attachment_name: f.name });
    reader.readAsDataURL(f);
  };

  const userName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? `${u.first_name} ${u.last_name}` : id;
  };

  return (
    <div className="space-y-6" data-testid="absences-root">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Personal</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Abwesenheiten</h2>
          <p className="text-sm text-slate-500 mt-1">Verbleibender Urlaub: {user?.vacation_days_per_year - (user?.vacation_taken || 0)} Tage</p>
        </div>
        <Button data-testid="abs-add-btn" onClick={() => setDialog(true)} className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Antrag stellen</Button>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {isManager && <TableHead>Mitarbeiter</TableHead>}
                <TableHead>Typ</TableHead>
                <TableHead>Zeitraum</TableHead>
                <TableHead>Umfang</TableHead>
                <TableHead>Grund</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid={`abs-row-${a.id}`}>
                  {isManager && <TableCell className="font-medium">{userName(a.user_id)}</TableCell>}
                  <TableCell>{TYPES.find((t) => t.id === a.type)?.label || a.type}</TableCell>
                  <TableCell className="text-sm">{a.start_date} → {a.end_date}</TableCell>
                  <TableCell>{a.mode === "hours" ? `${a.hours}h` : `${a.days} Tage`}</TableCell>
                  <TableCell className="text-sm text-slate-500 max-w-xs truncate">{a.reason || "—"}</TableCell>
                  <TableCell><Badge className={`${STATUS_COLORS[a.status]} hover:${STATUS_COLORS[a.status]}`}>{a.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {isManager && a.status === "pending" && (
                        <>
                          <Button data-testid={`abs-approve-${a.id}`} size="icon" variant="ghost" onClick={() => decide(a.id, true)}><Check className="w-4 h-4 text-emerald-600" /></Button>
                          <Button data-testid={`abs-reject-${a.id}`} size="icon" variant="ghost" onClick={() => decide(a.id, false)}><X className="w-4 h-4 text-rose-600" /></Button>
                        </>
                      )}
                      {(a.user_id === user.id && a.status === "pending") || isManager ? (
                        <Button size="icon" variant="ghost" onClick={() => del(a.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={isManager ? 7 : 6} className="text-center text-slate-500 py-8">Keine Anträge</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Abwesenheit beantragen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Typ</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="abs-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Von</Label><Input data-testid="abs-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Bis</Label><Input data-testid="abs-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div><Label>Grund / Notiz</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} /></div>
            <div>
              <Label>Anhang (eAU / Attest)</Label>
              <div className="flex items-center gap-2">
                <input type="file" onChange={onFile} className="text-sm" />
                {form.attachment_name && <span className="text-xs text-slate-500"><Paperclip className="w-3 h-3 inline" /> {form.attachment_name}</span>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button data-testid="abs-save" onClick={create}>Einreichen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
